// 모두싸인 템플릿 response
export type ModuSignTemplateModel = {
  count: number;
  templates: ModuSignTemplate[];
};

export type ModuSignTemplate = {
  id: string; // 템플릿 id
  title: string; // 템플릿 명
  participants: ModuSignParticipant[]; // 참여자 수
  updatedAt: string; // 템플릿 마지막 업데이트 시간
  createdAt: string; // 템플릿 생성 시간
};

// 서명 기한 정보
export type SigningDue = {
  valid: boolean; // 현재 서명 기한 여부
  datetime: string; // 서명자의 서명 유효일
};

// 서명 수단 정보
export type SigningMethod = {
  type: string; // 서명 수단 종류 (EMAIL, KAKAO, SECURE_LINK, IN_PERSON)
  value: string; // 서명 수단 값
};

// 참여자 정보
export type ModuSignParticipant = {
  id: string; // 참여자 ID
  type: string; // 참여자 타입 (SIGNER: 서명자, VIEWER: 열람자)
  role: string; // 참여자 역할
  name: string; // 참여자 이름
  signingOrder: number; // 서명 순서
  signingDue: SigningDue; // 서명 기한 정보
  signingMethod: SigningMethod; // 서명 수단 정보
  locale: string; // 참여자가 사용할 언어 (ko, en, zh-CN, ja, vi)
  requesterEditable: boolean; // 심세 열람 여부
};

// 파일 정보
export type File = {
  downloadUrl: string; // 다운로드 링크(유효시간: 10분)
};

// 메타데이터 정보
export type Metadatas = {
  dataLabel: string; // 데이터 라벨 (메타의 입력값)
  type: string; // 요청자 입력 타입
  value: string; // 필드에 들어갈 내용
};

// 요청자 첨부파일 정보
export type RequesterAttachments = {
  // 추가 정보가 필요하면 여기에 정의
};

// 요청자 입력 정보
export type RequesterInputs = {
  dataLabel: string; // 데이터 라벨 (메타의 입력값)
  type: string; // 요청자 입력 타입
  value: string; // 필드에 들어갈 내용
};

// 문서 라벨 정보
export type DocumentLabels = {
  id: string; // 라벨 ID
  name: string; // 라벨 이름
  description: string; // 라벨의 설명
};

// 참조자 리스트
export type CarbonCopies = {
  contact: string; // 참조자 연락처 (이메일 주소 / 휴대폰 번호)
  locale: string; // 참조자 언어 (ko, en, zh-CN, ja, vi)
};

// 모두싸인 템플릿 상세 정보
export type ModuSignTemplateInfo = {
  id: string; // 템플릿 ID
  title: string; // 템플릿명
  participants: ModuSignParticipant[]; // 참여자 리스트
  updatedAt: string; // 마지막 업데이트 시간
  createdAt: string; // 생성 시간
  file: File; // 템플릿 PDF 파일 정보
  metadatas: Metadatas[]; // 메타데이터 리스트(템플릿의 메타데이터는 순서가 보장되지 않습니다)
  requesterInputs: RequesterInputs[]; // 템플릿에 적용된 추가값을 입력
  requesterAttachments: RequesterAttachments[]; // 설정된 요청자 첨부파일 정보
  documentLabels: DocumentLabels[]; // 해당 템플릿으로 문서 요청 시 지정으로 적용될 라벨목록
  carbonCopiesList: CarbonCopies[]; // 참조자 리스트
  requesterEditable: boolean; // 템플릿으로 설정 요청 시 요청자 내용 변경 가능한 이유 (false 인 경우 기본값이 발송 설정될 제외하고, 서명자부 같은 데이터를 수정하여 발송할 수 없습니다)
};

// 문서 시작 요청 - 인증 정보
export type DocumentVerification = {
  password?: {
    value: string; // 비밀번호
    hint: string; // 비밀번호 힌트
    used?: boolean; // fe 적용
  };
  mobileIdentification?: {
    name: string; // 휴대폰 본인 인증에 필요한 이름
    phoneNumber: string; // 휴대폰 본인 인증에 필요한 전화번호
    used?: boolean; // fe 적용
  };
  dCert?: {
    name: string; // 범용 공인인증서 인증 이름
    bizNumber: string; // 범용 공인인증서 인증 사업자등록번호
    used?: boolean; // fe 적용
  };
};

// 문서 시작 요청 - 서명 방법
export type DocumentSigningMethod = {
  type: string; // 서명 수단 종류 (EMAIL, KAKAO, SECURE_LINK, IN_PERSON)
  value: string; // 서명 수단 값
};

// 문서 시작 요청 - 참여자 매핑
export type DocumentParticipantMapping = {
  role: string; // 템플릿에서 지정한 참여자 역할
  excluded?: boolean; // 템플릿에서 지정한 참여자 제외여부 (Defaults to false)
  name: string; // 지정한 참여자 이름 (예: "홍*동", /[*]/gm으로 길이 검증)
  signingMethod: DocumentSigningMethod;
  signingDuration?: number | string; // 참여자의 서명 유효 기간 (초) - 60~2520000[초], 미지정 시 기본값 20[일]
  requesterMessage?: string; // 참여자에 대한 발송자 메시지
  verification?: DocumentVerification;
  locale?: string; // 참여자가 사용할 언어 (ko, en, zh-CN, ja, vi)
};

// 문서 시작 요청
export interface ModuSignStartDocumentRequest {
  templateId: string; // 템플릿 ID
  document: {
    title: string; // 문서명
    fileOpenPassword?: string; // 문서파일(.pdf) 비밀번호
    participantMappingsList: DocumentParticipantMapping[];
    requesterInputMappingsList?: {
      dataLabel: string; // 데이터 라벨
      value: string; // 요청자 입력 매핑 정보
    }[];
    // 외부 참조자 리스트
    carbonCopiesList?: {
      contact: string; // 참조자 연락처 (이메일 주소 / 휴대폰 번호)
      locale: string; // 참조자 언어 (ko, en, zh-CN, ja, vi)
    }[];
    metadatas?: {
      key: string; // 메타 데이터 키 (partnerId, contractId)
      value: string; // 메타 데이터 값
    }[];
  };
}

// 모두싸인 문서 목록 타입 (RCS55)
export type ModuSignDocumentModel = {
  count: number;
  documents: ModuSignDocument[];
};

// 문서 상세 정보
export type ModuSignDocument = {
  id: string; // 문서 ID
  title: string; // 문서명
  status: ModuSignStatusType; // 문서 상태
  requester: {
    name: string; // 문서 요청자 이름
    email: string; // 문서 요청자 이메일
  };
  participants: ModuSignParticipant[]; // 참여자 리스트
  currentSigningOrder: number; // 현재 참여 차례
  accessibleByParticipant: boolean; // 문서 참여 시 요청자 위임 참여자들의 문서 파일 접근 기능 여부
  abort?: {
    participantId: string; // 중단한 참여자 ID (가끔 또는 없는 참여 요소 시)
    type:
      | "REJECTION" // 거절함
      | "SIGNING_CANCELLATION" // 참여 취소됨
      | "REQUEST_CANCELLATION"; // 요청 취소됨; // 중단 종류
    message: string; // 중단 사유
    abortedAt: string; // 중단 시점
  };
  updatedAt: string; // 마지막 업데이트 시간
  createdAt: string; // 생성 시간
  startedAt: string; // 참여 요청 시간
  metadatas: {
    key: string; // 메타 데이터 키 (partnerId, contractId)
    value: string; // 메타 데이터 값
  }[]; // 메타데이터 리스트(응답의 메타데이터는 순서가 보장되지 않습니다)
  labels: {
    id: string; // 라벨 ID
    name: string; // 라벨 이름
    description: string; // 라벨의 설명
  }[]; // 라벨 리스트(응답의 라벨은 순서가 보장되지 않습니다)
  file: File; // 문서 파일 정보
  auditTrail: File; // 감사 추적 인증서 정보
};

export type ModuSignStatusType = keyof typeof ModuSignDocumentStatus;

export const ModuSignDocumentStatus = {
  DRAFT: "작성 중",
  SCHEDULED: "전송 예약 중",
  ON_GOING: "서명/열람 진행 중",
  ON_PROCESSING: "문서 처리 중",
  PROCESSING_FAILED: "문서 처리 실패",
  ABORTED: "서명/열람 중단",
  COMPLETED: "모든 서명/열람 완료",
} as const;

export type ModuSignCancelDocument = {
  id: string; // 문서 ID
  message: string; // 취소 사유
  accessibleByParticipant?: boolean; // 문서 참여 시 요청자 위임 참여자들의 문서 파일 접근 기능 여부
};

export type ModuSignDocumentPreview = {
  embeddedId: string; //생성된 초안의 ID
  embeddedUrl: string; //	임베딩 가능한 서명 요청 주소(유효시간: 2시간)
  expiry: string; //생성된 초안의 만료 시간
  previewUrl: string; //생성된 초안의 미리보기 URL
};
